import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, Loader2, User } from 'lucide-react'
import Modal from '../ui/Modal'

export type BankPsuType = 'business' | 'personal'

/**
 * Autorizar el acceso al banco: qué va a pasar y con qué tipo de acceso.
 *
 * El Sabadell tiene DOS pantallas de acceso distintas para esto, la de banca de
 * EMPRESAS y la de particulares/autónomos, y Enable Banking decide cuál enseñar
 * según `psu_type`. Hasta el 21-10-2026 iba fijo a «empresas»: cuando esa pantalla
 * rechazó el usuario y la contraseña de Jesús y de Jorge —que sí entran bien
 * en la web del banco— no había forma de probar la otra desde la aplicación.
 *
 * Saca al usuario de JR2 OS, así que dice a dónde va y qué ve y qué no ve el CRM.
 */
export default function BankAuthModal({
  open,
  onClose,
  onChoose,
  busy,
}: {
  open: boolean
  onClose: () => void
  onChoose: (psuType: BankPsuType) => void
  busy?: boolean
}) {
  const { t } = useTranslation('banking')
  const [psuType, setPsuType] = useState<BankPsuType>('business')

  const option = (value: BankPsuType, Icon: typeof Building2) => (
    <label
      className={[
        'flex items-start gap-3 rounded-lg border p-3 cursor-pointer',
        psuType === value ? 'border-jr2-500 bg-jr2-500/10' : 'border-base hover:bg-hover',
      ].join(' ')}
    >
      <input
        type="radio"
        name="psuType"
        value={value}
        checked={psuType === value}
        onChange={() => setPsuType(value)}
        className="mt-1 accent-jr2-600"
      />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-semibold t-primary">
          <Icon size={14} />
          {t(`feed.authModal.${value}.label`)}
        </span>
        <span className="block text-xs t-muted mt-0.5">{t(`feed.authModal.${value}.hint`)}</span>
      </span>
    </label>
  )

  return (
    <Modal open={open} onClose={onClose} title={t('feed.authModal.title')} maxWidth="max-w-lg">
      <div className="space-y-4">
        <p className="text-sm t-secondary">{t('feed.authModal.intro')}</p>

        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider t-muted">{t('feed.authModal.pick')}</div>
          {option('business', Building2)}
          {option('personal', User)}
          <p className="text-xs t-muted">{t('feed.authModal.tryOther')}</p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onChoose(psuType)}
            disabled={busy}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {t('feed.authModal.go')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
